import { cva } from 'class-variance-authority'
import { isValidElement, type ReactElement, type ReactNode } from 'react'
import {
	Cell as AriaCell,
	Column as AriaColumn,
	Row as AriaRow,
	Table as AriaTable,
	TableBody as AriaTableBody,
	TableHeader as AriaTableHeader,
	type Key,
	type SortDescriptor,
} from 'react-aria-components'
import { cn } from '../../lib/cn'
import { focusRing } from '../../lib/focus-ring'
import type { StatusTone } from '../../atoms/status/status'
import { TableCell, tableCellVariants } from '../../atoms/table-cell/table-cell'

/**
 * The table of the Work Orders list (321:25524): a white card, 16px radius, 24px
 * padding, holding a header row of 48px Topic cells (TableCell `size="md"`) and
 * body rows of 69px Content, ID & Name, Status, Designer and Deadline cells, each
 * closed by its own Stroke rule. The Figma builds it column by column; here it is a real table
 * with the columns' widths fixed and the remaining width going to the columns
 * without one (the drawn ID & Name column).
 *
 * The Figma draws no sortable heading: a column with `allowsSorting` sorts on
 * press, and the sorted one shows the kit's arrow after its title.
 *
 * Rows have no hover or selected look in the Figma. A row with `onRowAction` is
 * a pointer target: its cells take the Background grey under the pointer, and the
 * row takes the kit's accent outline on keyboard focus, neither drawn in the Figma.
 */
export const tableVariants = cva('w-full rounded-atlas-lg bg-atlas-light p-6 font-atlas-sans')

/** What a body cell shows: one of the TableCell types, or any element on the 69px row. */
export type TableBodyCell =
	| { type: 'content'; label: string; emphasis?: boolean }
	| { type: 'id-name'; code: string; name: string }
	| { type: 'status'; label: string; tone?: StatusTone }
	| { type: 'designer'; name?: string; unassignedLabel?: string }
	| { type: 'deadline'; label: string; overdue?: boolean }
	| ReactElement

/** One column: its heading, its cell for a row, and optionally its width. */
export interface TableColumn<T> {
	id: Key
	title: string
	/** The column whose cell names the row for assistive technology; defaults to the first. */
	isRowHeader?: boolean
	/** Width utility for the column, e.g. `w-27.5` (110px); leave it out to share the rest. */
	className?: string
	/** The heading's sides: `narrow` is the Deadline column's 7px (321:25683), against 12px. */
	headerInset?: 'default' | 'narrow'
	/** Whether pressing the heading sorts by this column; the sorting itself is the caller's. */
	allowsSorting?: boolean
	cell: (row: T) => TableBodyCell
}

export interface TableProps<T extends { id: Key }> {
	/** The table's accessible name, e.g. "Work orders". */
	'aria-label': string
	columns: TableColumn<T>[]
	rows: T[]
	/** Called with the row's id when a row is pressed or Enter is pressed on it. */
	onRowAction?: (id: Key) => void
	/** The sorted column and its direction, shown as an arrow on its heading. */
	sortDescriptor?: SortDescriptor
	/** Called when a sortable heading is pressed: an unsorted column asks for ascending, the sorted one for the other direction. */
	onSortChange?: (descriptor: SortDescriptor) => void
	/** Shown in place of the rows when there are none; not drawn in the Figma. */
	emptyState?: ReactNode
	/** Pixels below which the table scrolls sideways instead of squeezing its auto-width column. */
	minWidth?: number
	className?: string
}

// Cells paint their own fill, so the row's hover is drawn on each cell. A row is
// only hovered when it is actionable, so a read-only table never changes colour.
const hoverClass = 'transition-colors group-data-hovered/row:bg-atlas-background'

function BodyCell({ content }: { content: TableBodyCell }) {
	if (isValidElement(content))
		return <div className={cn(tableCellVariants({ type: 'status' }), hoverClass)}>{content}</div>
	return <TableCell {...content} className={hoverClass} />
}

/**
 * A data table built on react-aria's Table: semantic table markup with column
 * headers, row headers and grid keyboard navigation. Columns and rows are props;
 * each column maps a row to a TableCell.
 */
export function Table<T extends { id: Key }>({
	columns,
	rows,
	onRowAction,
	sortDescriptor,
	onSortChange,
	emptyState,
	minWidth,
	className,
	'aria-label': ariaLabel,
}: TableProps<T>) {
	const rowHeader = columns.find((column) => column.isRowHeader)?.id ?? columns[0]?.id
	return (
		<div className={cn(tableVariants(), minWidth && 'overflow-x-auto', className)}>
			<AriaTable
				aria-label={ariaLabel}
				onRowAction={onRowAction}
				sortDescriptor={sortDescriptor}
				onSortChange={onSortChange}
				className="w-full table-fixed border-collapse"
				style={minWidth ? { minWidth } : undefined}
			>
				<AriaTableHeader columns={columns}>
					{(column) => (
						<AriaColumn
							id={column.id}
							isRowHeader={column.id === rowHeader}
							allowsSorting={column.allowsSorting}
							className={cn(
								focusRing,
								'p-0 text-left font-normal data-allows-sorting:cursor-pointer data-focus-visible:-outline-offset-2',
								column.className,
							)}
						>
							{({ sortDirection }) => (
								<TableCell
									type="topic"
									size="md"
									inset={column.headerInset}
									label={column.title}
									sortDirection={sortDirection}
								/>
							)}
						</AriaColumn>
					)}
				</AriaTableHeader>
				<AriaTableBody items={rows} renderEmptyState={emptyState ? () => emptyState : undefined}>
					{(row) => (
						<AriaRow
							id={row.id}
							columns={columns}
							className={cn(
								'group/row',
								focusRing,
								'data-focus-visible:-outline-offset-2',
								onRowAction && 'cursor-pointer',
							)}
						>
							{(column) => (
								<AriaCell className="p-0 outline-none">
									<BodyCell content={column.cell(row)} />
								</AriaCell>
							)}
						</AriaRow>
					)}
				</AriaTableBody>
			</AriaTable>
		</div>
	)
}
